import { BAUSTEIN_BASIS, BAUSTEIN_MODULE } from './portalBausteinSchema.ts';

/**
 * Freischalt-Stand der Baustein-Zeilen (Abschnitt E).
 * Klasse PORTAL_BAUSTEIN_FREISCHALTUNG.
 *
 * Quelle: Feld baustein_kategorien am customers-Sitz.
 * Schlüssel sind die Namen aus portalBausteinSchema.ts.
 * Wert freigeschaltet oder erwerbbar. Fehlendes Feld, leerer Wert,
 * unbekannte Kategorie = nicht frei. Ein Tarifname schaltet nichts frei.
 * module_judicature=true übernimmt nur JUDICATURE, solange der Schlüssel fehlt.
 * Die Fallanalyse-Live-Ansicht bleibt in diesem Modul unangetastet.
 */

export const BAUSTEIN_KATEGORIE_FIELD = 'baustein_kategorien';
export const BAUSTEIN_WERT_FREI = 'freigeschaltet';
export const BAUSTEIN_WERT_ERWERBBAR = 'erwerbbar';

export interface BausteinSeatInput {
  tier?: string | null;
  portalRole?: string | null;
  isPrimary?: boolean | null;
  includedSeats?: number | null;
  maxSeats?: number | null;
  kategorien?: Record<string, string> | string | null;
  moduleJudicature?: boolean | null;
}

export type BausteinUnlockKind = 'on' | 'off' | 'no-switch';

export interface BausteinUnlockResult {
  kind: BausteinUnlockKind;
  /** Vorhandener Schalter. null = dieser Name hat keinen. */
  switchId: string | null;
  /** Report-Satz, nicht die Kundenfläche. */
  report: string;
}

export const BAUSTEIN_STATUS_ON = 'freigeschaltet';
export const BAUSTEIN_STATUS_OFF = 'nicht freigeschaltet';

/** Look der Akte. Grün bleibt der Freischalt-Punkt. Die Fläche zeigt Zeilen, keine Karten. */
export const BAUSTEIN_NAME_ON = 'font-semibold text-violet-100';
export const BAUSTEIN_NAME_OFF = 'font-semibold text-slate-200';
export const BAUSTEIN_STATUS_CLASS_ON = 'mt-1 text-sm font-medium text-emerald-300';
export const BAUSTEIN_STATUS_CLASS_OFF = 'mt-1 text-sm text-slate-500';
export const BAUSTEIN_DOT_ON = 'mr-2 inline-block h-2.5 w-2.5 rounded-full bg-emerald-400 align-middle';

const SWITCH_KATEGORIE = BAUSTEIN_KATEGORIE_FIELD;
const SWITCH_LEGACY_JUDICATURE = 'module_judicature';

export const BAUSTEIN_BASIS_NAMEN: readonly string[] = BAUSTEIN_BASIS.map((row) => row.name);
export const BAUSTEIN_MODUL_NAMEN: readonly string[] = BAUSTEIN_MODULE.map((row) => row.name);

/** Angebotsvorlagen ohne Sitz-Kategorie. Basis und Module haben das Feld. */
export const BAUSTEIN_MISSING_SWITCH: Readonly<Record<string, string>> = {
  'Betrieb ARKORBIT': 'kein Betriebsart-Feld',
  'Hosting-Zeile': 'kein Betriebsart-Feld',
  'Setup / Box': 'kein Modul-Schalter',
  Server: 'kein Betriebsart-Feld',
  'Betrieb Kanzlei': 'kein Betriebsart-Feld',
  'Setup und Abnahme': 'kein Modul-Schalter',
  'Basis Betrieb ARKORBIT': 'kein Betriebsart-Feld',
};

function positiveInt(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;
  return Math.floor(value);
}

/** Nur gesetzte Zahlen. Ein Tarif füllt hier nichts auf. */
export function explicitSeatCeiling(seat: BausteinSeatInput): number | null {
  const maxSeats = positiveInt(seat.maxSeats);
  if (maxSeats != null) return maxSeats;
  return positiveInt(seat.includedSeats);
}

function parseKategorien(
  raw: BausteinSeatInput['kategorien'],
): Record<string, unknown> | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const text = raw.trim();
    if (!text) return null;
    try {
      const parsed = JSON.parse(text) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
      return parsed as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  return null;
}

function knownCategory(name: string): 'basis' | 'modul' | null {
  if (BAUSTEIN_BASIS_NAMEN.includes(name)) return 'basis';
  if (BAUSTEIN_MODUL_NAMEN.includes(name)) return 'modul';
  return null;
}

function storedValue(seat: BausteinSeatInput, name: string): string {
  const map = parseKategorien(seat.kategorien);
  if (!map || !Object.prototype.hasOwnProperty.call(map, name)) return '';
  const value = map[name];
  if (typeof value !== 'string') return '';
  return value.trim();
}

function categoryOn(name: string, seat: BausteinSeatInput): boolean {
  if (name === 'Support Plus') return false;
  return storedValue(seat, name) === BAUSTEIN_WERT_FREI;
}

function noSwitch(name: string): BausteinUnlockResult {
  return {
    kind: 'no-switch',
    switchId: null,
    report: BAUSTEIN_MISSING_SWITCH[name] ?? 'kein Schalter für diesen Namen',
  };
}

function off(report: string, switchId: string): BausteinUnlockResult {
  return { kind: 'off', switchId, report };
}

function on(report: string, switchId: string): BausteinUnlockResult {
  return { kind: 'on', switchId, report };
}

export function resolveBausteinUnlock(name: string, seat: BausteinSeatInput): BausteinUnlockResult {
  if (name === '2 Sitze extra') {
    const sitze = categoryOn('Sitze', seat);
    const ceiling = explicitSeatCeiling(seat);
    const extra = ceiling == null ? null : ceiling - 1;
    const unlocked = sitze && extra != null && extra >= 2;
    return unlocked
      ? on(`Sitze freigeschaltet und explizite Extra-Sitze ${extra} >= 2`, SWITCH_KATEGORIE)
      : off(
          'Modul Sitze nicht frei oder max_seats ergibt keine 2 Extra-Sitze (1 Admin abgezogen)',
          SWITCH_KATEGORIE,
        );
  }

  const kind = knownCategory(name);
  if (!kind) return noSwitch(name);

  if (name === 'Support Plus') {
    return off('Support Plus bleibt erwerbbar, bis der Dienst verkauft werden darf', SWITCH_KATEGORIE);
  }

  const value = storedValue(seat, name);
  if (value === BAUSTEIN_WERT_FREI) {
    return on(`${name} steht am Sitz auf freigeschaltet`, SWITCH_KATEGORIE);
  }
  if (name === 'JUDICATURE' && !value && seat.moduleJudicature === true) {
    return on('Bestand module_judicature=true übernimmt nur JUDICATURE', SWITCH_LEGACY_JUDICATURE);
  }
  if (!value) {
    return off(
      kind === 'basis'
        ? `${name} fehlt am Sitz — Rahmen ist nicht still frei`
        : `${name} fehlt oder ist leer — nicht freigeschaltet`,
      SWITCH_KATEGORIE,
    );
  }
  return off(`${name} steht auf ${value} — nicht freigeschaltet`, SWITCH_KATEGORIE);
}

/** Module, die dieser Sitz noch kaufen kann. Basis ist kein Kauf. */
export function bausteinErwerbbar(seat: BausteinSeatInput): string[] {
  return BAUSTEIN_MODUL_NAMEN.filter((name) => !categoryOn(name, seat));
}

export function bausteinRowIsGreen(result: BausteinUnlockResult): boolean {
  return result.kind === 'on';
}

/** Katalogname aus BAUSTEIN_MODULE, kein zweiter Produktname. */
export function judicatureModuleName(): string {
  const row = BAUSTEIN_MODULE.find((item) => item.name === 'JUDICATURE');
  if (!row) return '';
  return row.name;
}

/** Kurzer Satz, wenn das Modul auf dem Sitz nicht frei ist. Unpersönlich. */
export function judicatureSeatLockedSentence(): string {
  const name = judicatureModuleName();
  if (!name) return '';
  return `${name} ist in diesem Sitz nicht freigeschaltet.`;
}

const PREMIUM_UPSELL_RE =
  /\n*Für eine fallbezogene Analyse mit DOCX\/PDF nutzen Sie „OGH-Judikatur \/ Fallanalyse“ im Premium-Bereich\.?/g;

/**
 * Anzeige im ARKO-Chat. Tarifnamen Premium/Professional bleiben aus dem Text.
 * Eine Judikatur-Anfrage, die nicht lief, wird nicht nachträglich behauptet.
 */
export function arkoVisibleJudicatureText(
  text: string,
  trace?: { path?: string; warnings?: string[] } | null,
): string {
  const raw = String(text || '');
  const path = String(trace?.path || '');
  const warnings = Array.isArray(trace?.warnings) ? trace.warnings : [];
  const locked =
    path === 'premium_entitlement_gate'
    || warnings.includes('premium_required')
    || (/Orbis Premium/i.test(raw) && /Orbis Professional|JUDICATURE-Anfrage/i.test(raw));
  if (locked) return judicatureSeatLockedSentence();
  return raw.replace(PREMIUM_UPSELL_RE, '').replace(/\n{3,}/g, '\n\n').trim();
}
